import { useEffect, useMemo, useRef, useState } from "react";
import Grid from "@mui/material/Grid2";
import {
  Autocomplete,
  Button,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useSelector } from "react-redux";
import { useSearchParams } from "react-router-dom";
import AdvisorMapsTable from "./AdvisorMapsTable";
import AdvisorMapsTableSkeleton from "./AdvisorMapsTableSkeleton";
import { selectUserToken } from "../../../../features/userSlice";
import { AdvisorMapsEndpointLogic } from "../../../../logic/DataCollection/Advisor/AdvisorMapsEndPointLogic";

type AdvisorOption = { id: number; label: string };

const AdvisorMaps = () => {
  const oauthTokenHeader: any = useSelector(selectUserToken);
  const [searchParams, setSearchParams] = useSearchParams();

  const interfaceData = useSelector(
    (state: any) => state.advisor?.interfaceData,
  );
  const destinationTablesData = useSelector(
    (state: any) => state.advisor?.destinationTablesData,
  );

  const advisorMapsData = useSelector(
    (state: any) => state.advisor?.businessAdvisorMapsData,
  );

  const [filtersLoading, setFiltersLoading] = useState(true);
  const [mapsLoading, setMapsLoading] = useState(false);
  // the table stays hidden until "get data" comes back successfully
  const [dataLoaded, setDataLoaded] = useState(false);
  const [selectedInterface, setSelectedInterface] =
    useState<AdvisorOption | null>(null);
  const [selectedDestinationTable, setSelectedDestinationTable] =
    useState<AdvisorOption | null>(null);
  // the URL is only replayed once, so later edits are not undone
  const hasRestoredFromUrl = useRef(false);

  useEffect(() => {
    const fetchFilters = async () => {
      setFiltersLoading(true);
      try {
        await Promise.all([
          AdvisorMapsEndpointLogic.getInterface(oauthTokenHeader),
          AdvisorMapsEndpointLogic.getDestinationTables(oauthTokenHeader),
        ]);
      } finally {
        setFiltersLoading(false);
      }
    };
    fetchFilters();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const interfaceOptions: AdvisorOption[] = useMemo(
    () =>
      interfaceData?.map((item: any) => ({
        label: item?.affiliate_system_interface_name,
        id: item?.id,
      })) || [],
    [interfaceData],
  );

  const destinationTableOptions: AdvisorOption[] = useMemo(
    () =>
      destinationTablesData?.map((item: any) => ({
        label: item?.destination_table,
        id: item?.id,
      })) || [],
    [destinationTablesData],
  );

  const fetchMaps = async (
    interfaceOption: AdvisorOption,
    destinationTableOption: AdvisorOption | null,
  ) => {
    setMapsLoading(true);
    try {
      const isSuccess = await AdvisorMapsEndpointLogic.getBusinessAdvisorMaps(
        oauthTokenHeader,
        interfaceOption.id,
        destinationTableOption?.id,
      );
      setDataLoaded(isSuccess);
    } finally {
      setMapsLoading(false);
    }
  };

  useEffect(() => {
    if (filtersLoading || hasRestoredFromUrl.current) return;
    if (!interfaceOptions.length) return;
    hasRestoredFromUrl.current = true;
    const interfaceIDFromUrl = searchParams.get("interfaceID");
    if (!interfaceIDFromUrl) return;
    const restoredInterface =
      interfaceOptions.find(
        (option) => String(option.id) === interfaceIDFromUrl,
      ) ?? null;
    if (!restoredInterface) return;
    const destinationTableIDFromUrl = searchParams.get("destinationTableID");
    const restoredDestinationTable =
      destinationTableOptions.find(
        (option) => String(option.id) === destinationTableIDFromUrl,
      ) ?? null;
    setSelectedInterface(restoredInterface);
    setSelectedDestinationTable(restoredDestinationTable);
    fetchMaps(restoredInterface, restoredDestinationTable);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtersLoading, interfaceOptions, destinationTableOptions]);

  const handleSelectInterface = (newValue: AdvisorOption | null) => {
    setSelectedInterface(newValue);
    setSearchParams(
      (prevParams) => {
        const params = new URLSearchParams(prevParams);
        if (newValue) {
          params.set("interface", newValue.label);
          params.set("interfaceID", String(newValue.id));
        } else {
          params.delete("interface");
          params.delete("interfaceID");
        }
        return params;
      },
      { replace: true },
    );
    // results belong to the interface they were fetched for
    if (!newValue) setDataLoaded(false);
  };

  const handleSelectDestinationTable = (newValue: AdvisorOption | null) => {
    setSelectedDestinationTable(newValue);
    setSearchParams(
      (prevParams) => {
        const params = new URLSearchParams(prevParams);
        if (newValue) {
          params.set("destinationTable", newValue.label);
          params.set("destinationTableID", String(newValue.id));
        } else {
          params.delete("destinationTable");
          params.delete("destinationTableID");
        }
        return params;
      },
      { replace: true },
    );
  };

  const handleGetData = () => {
    if (!selectedInterface) return;
    setSearchParams(
      (prevParams) => {
        const params = new URLSearchParams(prevParams);
        params.set("page", "1");
        return params;
      },
      { replace: true },
    );
    fetchMaps(selectedInterface, selectedDestinationTable);
  };

  return (
    <>
      <Grid container alignItems="flex-start" columnSpacing={3} sx={{ mt: 1 }}>
        <Grid size={{ xs: 12, md: 5 }}>
          <Typography variant="h3" sx={{ fontSize: "24px", mb: 0.5, mt: 1 }}>
            Map List
          </Typography>
          <Typography variant="body2" sx={{ fontSize: "14px" }}>
            Each map decides, per interface and destination, which canonical
            metrics are derived by a gap rule and which come straight from the
            source.
          </Typography>
        </Grid>
        <Grid size={{ xs: 12, md: 7 }}>
          <Stack
            direction="row"
            alignItems="flex-start"
            justifyContent="flex-end"
            gap={2}
            sx={{ mt: 1 }}
          >
            <Autocomplete
              sx={{ flex: 1 }}
              options={interfaceOptions}
              data-tracker="942"
              loading={filtersLoading}
              value={selectedInterface}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              onChange={(_, newValue) => handleSelectInterface(newValue)}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Interface"
                  placeholder={
                    filtersLoading ? "Loading..." : "Choose from the list"
                  }
                  helperText="Mandatory Field"
                  required
                  color="secondary"
                />
              )}
              size="small"
            />
            <Autocomplete
              sx={{ flex: 1 }}
              options={destinationTableOptions}
              data-tracker="943"
              loading={filtersLoading}
              value={selectedDestinationTable}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              onChange={(_, newValue) => handleSelectDestinationTable(newValue)}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Destination Table"
                  placeholder={
                    filtersLoading ? "Loading..." : "Choose from the list"
                  }
                  color="secondary"
                />
              )}
              size="small"
            />
            <Button
              variant="contained"
              color="secondary"
              disabled={!selectedInterface || mapsLoading}
              onClick={handleGetData}
              data-tracker="944"
              sx={{
                height: "40px",
                padding: "8px 22px",
                flexShrink: 0,
                whiteSpace: "nowrap",
              }}
            >
              Get Data
            </Button>
          </Stack>
        </Grid>
      </Grid>

      {mapsLoading ? (
        <AdvisorMapsTableSkeleton
          rows={parseInt(searchParams.get("rowsPerPage") ?? "10", 10) || 10}
        />
      ) : (
        dataLoaded && (
          <AdvisorMapsTable advisorMapsData={advisorMapsData ?? []} />
        )
      )}
    </>
  );
};

export default AdvisorMaps;
